import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import TagManager from 'react-gtm-module';
import { Outlet, useNavigation } from 'react-router-dom';

import { isPageWithoutAutorization } from '../constants/routes';
import Tracking from './Tracking/Tracking';
import { GlobalLoader } from './GlobalLoader';
import { checkFinalRegistrationRule, getCurrentUserProfileNew } from '../actions/profileActions';
import { getCurrentUserContextOrganizations } from '../actions/contextActions';
import { EAlertType } from '../entities/IAlert';
import { useAlert } from '../tools/hooks';
import { addRcivedNotification, getNotificationsActiveData } from '../actions/notificationsActions';
import { useNotification } from '../tools/signalRHook';
import { ISingleConversation, ISingleMessage } from '../entities/Messaging/IMessaging';
import { IClusterSingleMessage, IClusterValidationRequestStatusUpdateMessage, ISingleChannelMessage } from '../entities/IClusters';
import { IState } from '../reducers';
import { IClusterReducer } from '../reducers/clustersReducer';
import { activityCreatedEventOn, addReceiveAttachment, addUnreadConversation, clearCurrentMessages, newEventsHaveOccuredOn, removeTempConversation, setActiveConversation, updateSingleConversation, updateSingleMessage, userRemovedFromConversation } from '../actions/messagingActions';
import { deleteChannelThreadFromState, getClusterDocuments, getSingleCluster, receiveClusterMessage, receiveThreadMessage } from '../actions/clustersActions';
import { checkUserHasActiveApprovalRequests, getSingleLastValidation } from '../actions/validationActions';
import { EApprovalTypes } from '../actions/actionTypes/approvalTypes';
import { IProfileReducer } from '../reducers/profileReducer';
import { IMessagingReducer } from '../reducers/messagingReducer';
import { ENotificationType } from '../entities/INotification';
import { EBranchNotificationType } from '../entities/ISoge';
import { IContextReducer } from '../reducers/contextReducer';
import { ICookiesReducer } from '../reducers/cookiesReducer';
import { ETrackingType } from '../entities/IAnalytics';
import { IAnalyticsReducer } from '../reducers/analyticsReducer';
import { ILanguageReducer } from '../reducers/languageReducer';
import { AlertContainer } from './Common/Alert/AlertContainer';
import { getAllReferentials } from '../actions/globalActions';
import { IGlobalReducer } from '../reducers/globalReducer';
import IntlMessage, { useIntlMessage } from './Common/IntlMessage';
import { TopBar } from './Layout/TopBar/TopBar';
import { SideBar } from './Layout/SideBar/SideBar';
import { PanelContainer } from './Common/Panel/PanelContainer';
import { OverlayContextProvider } from './NewDesignCommon/Overlay';
import { useClusterContext } from './Routes/Workspace/Cluster/ClusterContext';
import { NoData } from './Common/NoData/NoData';
import { isTeamsInstance } from '../tools/runtimeInstance';
import { SignalRRoomSubscriptions } from '../tools/SignalRRoomSubscriptions';
import { selectClientLogo, selectGtmEnabled, selectRegistrationFinalScreenEnabled } from '../tools/platformConfiguration';
import { getClusterIdFromAssignToClusterCompleted } from '../tools/clusterCreationWait';
import { PlatformDisabledFeatureRedirect } from './Common/PlatformDisabledFeatureRedirect';

type CheckFinalRegistrationRule = ReturnType<typeof checkFinalRegistrationRule>;
type GetCurrentUserContextOrganizations = ReturnType<typeof getCurrentUserContextOrganizations>;
type GetCurrentUserProfileNew = ReturnType<typeof getCurrentUserProfileNew>;
type GetNotificationsActiveData = ReturnType<typeof getNotificationsActiveData>;
type NewEventsHaveOccuredOn = ReturnType<typeof newEventsHaveOccuredOn>;
type ActivityCreatedEventOn = ReturnType<typeof activityCreatedEventOn>;
type ReceiveThreadMessage = ReturnType<typeof receiveThreadMessage>;
type GetClusterDocuments = ReturnType<typeof getClusterDocuments>;
type ReceiveClusterMessage = ReturnType<typeof receiveClusterMessage>;
type GetSingleLastValidation = ReturnType<typeof getSingleLastValidation>;
type CheckUserHasActiveApprovalRequests = ReturnType<typeof checkUserHasActiveApprovalRequests>;
type GetSingleCluster = ReturnType<typeof getSingleCluster>;
type DeleteChannelThreadFromState = ReturnType<typeof deleteChannelThreadFromState>;
type AddReceiveAttachment = ReturnType<typeof addReceiveAttachment>;
type AddUnreadConversation = ReturnType<typeof addUnreadConversation>;
type UpdateSingleConversation = ReturnType<typeof updateSingleConversation>;
type SetActiveConversation = ReturnType<typeof setActiveConversation>;
type UpdateSingleMessage = ReturnType<typeof updateSingleMessage>;
type RemoveTempConversation = ReturnType<typeof removeTempConversation>;
type AddRcivedNotification = ReturnType<typeof addRcivedNotification>;
type UserRemovedFromConversation = ReturnType<typeof userRemovedFromConversation>;
type ClearCurrentMessages = ReturnType<typeof clearCurrentMessages>;
type GetAllReferentials = ReturnType<typeof getAllReferentials>;

const scrollExceptions: (string | RegExp)[] = [/\/rfp/];

/** Cluster id from URL when path is /orgs/:org/cluster/:id/... or /teams/orgs/:org/cluster/:id/... */
const getClusterIdFromPathname = (pathname: string): string | null => {
    const match = pathname.match(/\/cluster\/([^/]+)/);
    return match?.[1] ?? null;
};

export const GlobalLayout: FC = props => {
    const navigation = useNavigation();
    const dispatch = useDispatch();
    const { intlFormatMessage } = useIntlMessage();
    const addAlert = useAlert();
    const clusterContext = useClusterContext();
    const { clusterValidationRequestStatusUpdateMessageDto, conversationMemberRemoved, receiveNotification, receiveMessage, receiveChannelMessage, updateChannelMessage, assignedRoleCreated, assignToClusterCompleted, deleteChannelMessage, receiveConversation, attachmentUploaded, recalculateMyValidationRequestsTick } = useNotification();
    const { isLoggedIn, isInExportView, translations, tracker, userLanguage, cookieBlock, isShadowAuth, conversations, activeConversationId, showConversationMenu, currentChannelId, currentUserProfile } = useSelector<IState, IGlobalReducer & ICookiesReducer & IContextReducer & IMessagingReducer & IClusterReducer & IProfileReducer & IAnalyticsReducer & ILanguageReducer>(state => ({
        ...state.clusters,
        ...state.profile,
        ...state.context,
        ...state.messaging,
        ...state.cookies,
        ...state.analytics,
        ...state.language,
        ...state.global
    }));
    const paramsUrl = new URLSearchParams(window.location.search);
    const [loading, setLoading] = useState<boolean>(true);
    const [currentUrl, setCurrentUrl] = useState<string>('');
    const [initialOperations, setInitialOperations] = useState<boolean>(false);
    const [loginInfo, setLoginInfo] = useState<boolean>(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
    const gtmEnabled = useSelector(selectGtmEnabled);
    const clientLogo = useSelector(selectClientLogo);
    const registrationFinalScreenEnabled = useSelector(selectRegistrationFinalScreenEnabled);

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.setProperty('overflow', 'hidden');
        } else {
            document.body.style.removeProperty('overflow');
        }
    }, [isMobileMenuOpen]);

    useEffect(() => {
        if (!isShadowAuth && !initialOperations && !isPageWithoutAutorization() && userLanguage && Object.keys(translations)?.length > 0) {

            if (!currentUserProfile) {
                setLoading(false);
            } else {
                dispatch<GetAllReferentials>(getAllReferentials());
                Promise.all([
                    dispatch<CheckFinalRegistrationRule>(checkFinalRegistrationRule()),
                    dispatch<GetCurrentUserContextOrganizations>(getCurrentUserContextOrganizations())
                ]).then(response => {
                    if (registrationFinalScreenEnabled && !currentUserProfile.isProfileValidated && response[0] && window.location.pathname !== '/registration-final-step') {
                        window.location.replace('/registration-final-step');
                    } else {
                        setLoading(false);
                        !paramsUrl.get('token') && dispatch<GetNotificationsActiveData>(getNotificationsActiveData());
                    }
                    if (currentUserProfile && !loginInfo) {
                        if (currentUserProfile.lastLoginDate && !window.location?.search?.includes('wopieItemId') && !isTeamsInstance() && window.location.pathname !== '/registration-final-step') {
                            addAlert(<span dangerouslySetInnerHTML={{
                                __html: intlFormatMessage({ id: 'admin.lastLoginDate' }, {
                                    userName: currentUserProfile.firstName,
                                    lastLoginDate: moment.utc(currentUserProfile.lastLoginDate).local().format('DD MMM YYYY HH:mm:ss')
                                })
                            }} />, EAlertType.INFO);
                            setLoginInfo(true);
                        }
                    }
                });
            }
            if (cookieBlock === false) {
                const tagManagerArgs = {
                    gtmId: 'GTM-WF74HF6'
                };
                gtmEnabled && TagManager.initialize(tagManagerArgs);
            }
            setInitialOperations(true);
        } else if (isShadowAuth) {
            dispatch<GetCurrentUserContextOrganizations>(getCurrentUserContextOrganizations());
            setLoading(false);
        }
        if (isPageWithoutAutorization()) {
            setLoading(false);
        }
        setLoading(false);
    }, [translations, userLanguage, currentUserProfile, registrationFinalScreenEnabled]);

    useEffect(() => {
        const currentLocation = window.location;
        const currentLocalUrl = `${currentLocation.pathname}${currentLocation.search || ''}`;
        setCurrentUrl(currentLocalUrl);
        if (currentUrl !== currentLocalUrl) {
            if (!scrollExceptions.find(exception => !!currentUrl.match(exception))) {
                window.scrollTo(0, 0);
            }

            if (tracker[ETrackingType.Smh]) {
                tracker[ETrackingType.Smh].setCustomUrl(currentUrl);
                tracker[ETrackingType.Smh].trackPageView();
            }
        }
    }, [window.location]);

    useEffect(() => {
        if (receiveChannelMessage) {
            receiveChannelMessageHandler(receiveChannelMessage);
        }
    }, [receiveChannelMessage]);

    useEffect(() => {
        if (updateChannelMessage) {
            updateChannelMessageHandler(updateChannelMessage);
        }
    }, [updateChannelMessage]);

    useEffect(() => {
        if (assignedRoleCreated) {
            assignedRoleCreatedHandler(assignedRoleCreated);
        }
    }, [assignedRoleCreated]);

    useEffect(() => {
        if (deleteChannelMessage) {
            deleteChannelMessageHandler(deleteChannelMessage);
        }
    }, [deleteChannelMessage]);

    useEffect(() => {
        if (receiveMessage) {
            receiveMessageHandler(receiveMessage);
        }
    }, [receiveMessage]);

    useEffect(() => {
        if (receiveNotification) {
            receiveNotificationHandler(receiveNotification);
        }
    }, [receiveNotification]);

    useEffect(() => {
        if (conversationMemberRemoved) {
            conversationMemberRemovedHandler(conversationMemberRemoved);
        }
    }, [conversationMemberRemoved]);

    useEffect(() => {
        if (clusterValidationRequestStatusUpdateMessageDto) {
            clusterValidationRequestStatusUpdateMessageDtoHandler(clusterValidationRequestStatusUpdateMessageDto);
        }
    }, [clusterValidationRequestStatusUpdateMessageDto]);

    useEffect(() => {
        if (recalculateMyValidationRequestsTick <= 0 || !currentUserProfile?.id) {
            return;
        }
        const userId = currentUserProfile.id;
        const timeoutId = globalThis.setTimeout(() => {
            dispatch<CheckUserHasActiveApprovalRequests>(checkUserHasActiveApprovalRequests(userId)).catch(() => {});
        }, 400);
        return () => globalThis.clearTimeout(timeoutId);
    }, [recalculateMyValidationRequestsTick, currentUserProfile?.id, dispatch]);

    useEffect(() => {
        if (receiveConversation) {
            dispatch<NewEventsHaveOccuredOn>(newEventsHaveOccuredOn());
        }
    }, [receiveConversation]);

    useEffect(() => {
        if (attachmentUploaded) {
            dispatch<NewEventsHaveOccuredOn>(newEventsHaveOccuredOn());
            dispatch<AddReceiveAttachment>(addReceiveAttachment(attachmentUploaded.id, attachmentUploaded.attachments[0]));
        }
    }, [attachmentUploaded]);

    useEffect(() => {
        const completedClusterId = getClusterIdFromAssignToClusterCompleted(assignToClusterCompleted);
        if (completedClusterId) {
            const clusterIdFromUrl = getClusterIdFromPathname(window.location.pathname);
            const isViewingThisCluster =
                clusterContext.currentClusterId === completedClusterId ||
                clusterIdFromUrl === completedClusterId;
            if (isViewingThisCluster) {
                dispatch<GetSingleCluster>(getSingleCluster(completedClusterId)).then((res) => {
                    if (res?.id) {
                        clusterContext.setCurrentCluster(res);
                        clusterContext.setClusterPermissions(res.permissions ?? []);
                    }
                });
            }
        }
    }, [assignToClusterCompleted, clusterContext.currentClusterId]);

    const clusterValidationRequestStatusUpdateMessageDtoHandler = (message: IClusterValidationRequestStatusUpdateMessage) => {
        const clusterIdFromUrl = getClusterIdFromPathname(window.location.pathname);
        const isViewingThisCluster =
            message.additionalInformation?.clusterId === clusterContext.currentClusterId ||
            message.additionalInformation?.clusterId === clusterIdFromUrl;
        if (isViewingThisCluster && message.additionalInformation?.clusterId) {
            dispatch<GetSingleCluster>(getSingleCluster(message.additionalInformation.clusterId)).then((res) => {
                if (res?.id) {
                    clusterContext.setCurrentCluster(res);
                    clusterContext.setClusterPermissions(res.permissions ?? []);
                }
            });
            dispatch<GetSingleLastValidation>(getSingleLastValidation(message.additionalInformation.clusterId, EApprovalTypes.ClusterOverview));
        }
    };

    const conversationMemberRemovedHandler = (message) => {
        if (currentUserProfile.id === message.id) {
            dispatch<UserRemovedFromConversation>(userRemovedFromConversation(message.conversationId));

            if (conversations && conversations.length && activeConversationId && activeConversationId === message.conversationId) {
                const firstConv = conversations.filter((conversation: ISingleConversation) => conversation.id !== message.conversationId);
                const id = firstConv && firstConv[0] && firstConv[0].id;
                dispatch<SetActiveConversation>(setActiveConversation(id));
                dispatch<ClearCurrentMessages>(clearCurrentMessages());
                /* getMessages(); */
            }
        }
    };

    const receiveNotificationHandler = (message) => {
        const notificationTranslation = <IntlMessage
            id={`notificationsLabels.${message?.key}`}
            values={{
                ObjectName: message?.data?.ObjectName,
                LanguageCode: message?.data?.LanguageCode,
                BranchName: message?.data?.BranchName
            }}
        />;

        if (message?.data?.IsSuccess || message?.key === EBranchNotificationType?.AutomaticallyCreatedBranch) {
            addAlert(notificationTranslation, EAlertType.SUCCESS);
        } else if (message?.data?.IsSuccess === false) {
            addAlert(notificationTranslation, EAlertType.ERROR);
        }
        if (message?.key === ENotificationType.AddMemberToOrganization) {
            dispatch<GetCurrentUserContextOrganizations>(getCurrentUserContextOrganizations());
        }
        if (message?.key === ENotificationType.RemoveMemberFromOrganization) {
            dispatch<GetCurrentUserContextOrganizations>(getCurrentUserContextOrganizations());
        }
        if (message?.key === ENotificationType.AddMemberToGroup) {
            dispatch<GetCurrentUserProfileNew>(getCurrentUserProfileNew());
        }
        if (message?.key === ENotificationType.SideLetterGenerateStarted) {
            addAlert(notificationTranslation, EAlertType.SUCCESS);
        }
        if (message?.key === ENotificationType.SideLetterGenerateCompleted) {
            addAlert(notificationTranslation, EAlertType.SUCCESS);
        }
        if (message?.key === ENotificationType.SideLetterGenerateFailed) {
            addAlert(notificationTranslation, EAlertType.ERROR);
        }
        if (message?.key === ENotificationType.InvestorCommentGenerateStarted) {
            addAlert(notificationTranslation, EAlertType.SUCCESS);
        }
        if (message?.key === ENotificationType.InvestorCommentGenerateCompleted) {
            addAlert(notificationTranslation, EAlertType.SUCCESS);
        }
        if (message?.key === ENotificationType.InvestorCommentGenerateFailed) {
            addAlert(notificationTranslation, EAlertType.ERROR);
        }
        dispatch<AddRcivedNotification>(addRcivedNotification(message));
    };

    const receiveMessageHandler = (message: ISingleMessage) => {
        const isMyMessage = message.senderUserId === currentUserProfile?.id;
        !isMyMessage && dispatch<AddUnreadConversation>(addUnreadConversation(message.conversationId));
        dispatch<NewEventsHaveOccuredOn>(newEventsHaveOccuredOn());
        if (!(activeConversationId || showConversationMenu)) {
            return;
        }
        const conversationElem = (conversations || []).find((conversation: ISingleConversation) => conversation.id === message.conversationId);
        if (!conversationElem) {
            /* TODO: waiting for api this.props.getConversation(message.conversationId).then(conversation => {
                this.updateConversations(message, conversation);
            }); */
        } else {
            updateConversations(message, conversationElem);
        }
    };

    const updateConversations = (message: ISingleMessage, conversationElem: ISingleConversation) => {

        const dontUpdateUnreadMessagesCount = message.conversationId === activeConversationId || message.senderUserId === currentUserProfile?.id;

        const newConversationData = {
            ...conversationElem,
            unreadMessagesCount: dontUpdateUnreadMessagesCount ? 0 : (conversationElem?.unreadMessagesCount || 0) + 1,
            lastMessage: {
                creationDate: message.createdDate,
                messageId: message.id,
                preview: message.content.slice(0, 15) || ''
            }
        };

        if (message.tempId && conversations?.find((conversation: ISingleConversation) => conversation.id === message.tempId)) {
            dispatch<RemoveTempConversation>(removeTempConversation(message.tempId));
        }

        if ((message.conversationId === activeConversationId || message.tempId === activeConversationId) && message.type !== 'SystemRemovingMessage') {
            dispatch<UpdateSingleConversation>(updateSingleConversation(newConversationData));
            dispatch<SetActiveConversation>(setActiveConversation(message.conversationId));
        } else if (message.type !== 'SystemRemovingMessage') {
            dispatch<UpdateSingleConversation>(updateSingleConversation(newConversationData));
            /* if (!activeConversationId?.includes('tempGroup-')) {
                this.props.getMessages();
            } */
        }

        if (message.type === 'SystemRemovingMessage') {
            dispatch<UpdateSingleMessage>(updateSingleMessage(message));
        }
    };

    const deleteChannelMessageHandler = (message: IClusterSingleMessage) => {
        if (!message.isThread) {
            dispatch<ReceiveThreadMessage>(receiveThreadMessage(message));
        } else {
            dispatch<DeleteChannelThreadFromState>(deleteChannelThreadFromState(message.threadId));
        }
    };

    const assignedRoleCreatedHandler = (message) => {
        if (message.resource?.resourceType === 'Cluster' && message.resource?.childId) {
            dispatch<NewEventsHaveOccuredOn>(newEventsHaveOccuredOn());
            const clusterIdFromUrl = getClusterIdFromPathname(window.location.pathname);
            const isViewingThisCluster =
                message.resource.childId === clusterContext.currentClusterId ||
                message.resource.childId === clusterIdFromUrl;
            if (isViewingThisCluster) {
                dispatch<GetSingleCluster>(getSingleCluster(message.resource.childId)).then((response) => {
                    if (response?.id) {
                        clusterContext.setCurrentCluster(response);
                        clusterContext.setClusterPermissions(response.permissions ?? []);
                    }
                });
            }
        }
    };

    const updateChannelMessageHandler = (message: IClusterSingleMessage) => {
        const clusterIdFromUrl = getClusterIdFromPathname(window.location.pathname);
        const isViewingThisCluster =
            message.clusterId === clusterContext.currentClusterId || message.clusterId === clusterIdFromUrl;
        if (isViewingThisCluster) {
            dispatch<NewEventsHaveOccuredOn>(newEventsHaveOccuredOn());
            dispatch<GetSingleCluster>(getSingleCluster(message.clusterId)).then((response) => {
                if (response?.id) {
                    clusterContext.setCurrentCluster(response);
                    clusterContext.setClusterPermissions(response.permissions ?? []);
                }
            });
        }
        const ValidationRequestPreview = 'ValidationRequestPreview';
        if (message.type === ValidationRequestPreview && isViewingThisCluster) {
            dispatch<GetSingleLastValidation>(getSingleLastValidation(message.clusterId, EApprovalTypes.ClusterOverview));
        }
    };

    const receiveChannelMessageHandler = (message: IClusterSingleMessage) => {
        const clusterIdFromUrl = getClusterIdFromPathname(window.location.pathname);
        const isViewingThisCluster =
            message.clusterId === clusterContext.currentClusterId || message.clusterId === clusterIdFromUrl;
        const ValidationRequestPreview = 'ValidationRequestPreview';
        if (message.type === ValidationRequestPreview && isViewingThisCluster) {
            dispatch<GetSingleCluster>(getSingleCluster(message.clusterId)).then((response) => {
                if (response?.id) {
                    clusterContext.setCurrentCluster(response);
                    clusterContext.setClusterPermissions(response.permissions ?? []);
                }
            });
            dispatch<GetSingleLastValidation>(getSingleLastValidation(message.clusterId, EApprovalTypes.ClusterOverview));
        }
        if (isViewingThisCluster) {
            dispatch<NewEventsHaveOccuredOn>(newEventsHaveOccuredOn());
            dispatch<ActivityCreatedEventOn>(activityCreatedEventOn());
        }
        if (currentChannelId === message.teamChannelId) {
            if (!message.isThread) {
                dispatch<ReceiveThreadMessage>(receiveThreadMessage(message));
            } else {
                const messageObj: ISingleChannelMessage = {
                    ...message,
                    threadMessages: []
                };
                dispatch<ReceiveClusterMessage>(receiveClusterMessage(messageObj));
                if (message.type === 'SystemDocumentsUploaded') {
                    dispatch<GetClusterDocuments>(getClusterDocuments(clusterContext.currentClusterId));
                }
            }
        }
    };

    const showNavigation = useMemo(() => !(!(!isLoggedIn) && (window.location.pathname === '/register' || window.location.pathname === '/registration-final-step')) && !isShadowAuth && !isInExportView, [isLoggedIn, isInExportView, window.location.pathname, isShadowAuth]);
    const toggleMobileMenu = () => setIsMobileMenuOpen(isOpen => !isOpen);
    return (
        <OverlayContextProvider>
            <SignalRRoomSubscriptions />
            {!paramsUrl.get('token') &&
                <>
                    {Object.keys(translations)?.length > 0 &&
                        <AlertContainer />
                    }
                    <Tracking />
                </>
            }
            <PanelContainer />
            {(showNavigation) && (
                <TopBar onMobileMenuClick={toggleMobileMenu} isLoggedIn={isLoggedIn} clientLogo={clientLogo} />
            )}
            {(!isShadowAuth && !isInExportView && window.location.pathname !== '/register' && window.location.pathname !== '/registration-final-step' && Object.keys(translations)?.length > 0) &&
                <SideBar isMobileMenuVisible={false} />
            }
            <PlatformDisabledFeatureRedirect>
                {props.children || <Outlet />}
            </PlatformDisabledFeatureRedirect>
            {(!currentUserProfile && Object.keys(translations)?.length > 0 && !isInExportView && !isPageWithoutAutorization()) &&
                <NoData>
                    <IntlMessage id="global.genericError" />
                </NoData>
            }
            <GlobalLoader isLoading={loading || navigation.state !== 'idle'} />
        </OverlayContextProvider>
    );
};